import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Button, ControlledInput, useZodHookForm, VerticalSpacing, z } from '@willow/shared-web';
import { Form } from '@willow/shared-web/bootstrap';
import { PORTAL_LOGIN_PATH, PortalLoginConfigEnabled } from '@willow/types-iso';

import { authEmailSchema } from '../../auth/authFormSchema';
import { AuthLayout } from '../../auth/AuthLayout';
import { requestPasswordReset } from '../../auth/portalAuthApi';
import { Alert } from '../../components/alert/Alert';

const FormShape = z.object({ email: authEmailSchema });

const RESEND_COOLDOWN_MS = 30 * 1000;

interface Props {
  config: PortalLoginConfigEnabled;
}

export const ForgotPasswordPage = ({ config }: Props) => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  const [sentTo, setSentTo] = useState<string>();
  const [failed, setFailed] = useState(false);
  const [resending, setResending] = useState(false);
  const [inCooldown, setInCooldown] = useState(false);

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
  } = useZodHookForm(FormShape, {
    defaultValues: { email: params.get('email')?.trim() ?? '' },
  });

  useEffect(() => {
    if (!inCooldown) return undefined;
    const timer = window.setTimeout(() => setInCooldown(false), RESEND_COOLDOWN_MS);
    return () => window.clearTimeout(timer);
  }, [inCooldown]);

  // A 4xx answer is shown as a send too. Telling the borrower whether the server found
  // anything would turn this form into an account-existence oracle.
  const send = async (email: string): Promise<boolean> => {
    const result = await requestPasswordReset({ email });
    const transportFailure = !result.ok && (result.code === 'NETWORK' || (result.status ?? 500) >= 500);
    setFailed(transportFailure);
    if (!transportFailure) {
      setSentTo(email);
    }
    return !transportFailure;
  };

  const onSubmit = handleSubmit(async (data) => {
    await send(data.email);
  });

  // The cooldown starts on a resend rather than on the first send, so a borrower does not arrive
  // at the confirmation to find the only action on it already disabled.
  const onResend = async () => {
    if (!sentTo || resending || inCooldown) return;
    setResending(true);
    try {
      if (await send(sentTo)) {
        setInCooldown(true);
      }
    } finally {
      setResending(false);
    }
  };

  const errorAlert = failed && (
    <div className="auth-layout__aligned" role="alert" aria-live="assertive">
      <Alert level="ERROR" title="We could not send the email right now." subtitle="Please try again in a moment." />
    </div>
  );

  if (sentTo) {
    return (
      <AuthLayout
        portal={config.portal}
        documentTitle="Check your email"
        title="Reset instructions sent"
        // Rendered identically whether or not the address has a login, and the address is only
        // what the borrower typed, so this stays uniform and tells an attacker nothing.
        description={<>Please check {sentTo} for instructions to reset your password.</>}
      >
        <VerticalSpacing spacing={2}>
          {errorAlert}
          <Button
            size="lg"
            className="w-100"
            innerClassName="justify-content-center"
            onClick={onResend}
            disabled={inCooldown}
            loading={resending}
          >
            Resend email
          </Button>
          <Link className="auth-layout__link auth-layout__link--center" to={PORTAL_LOGIN_PATH}>
            Back to login
          </Link>
        </VerticalSpacing>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      portal={config.portal}
      documentTitle="Reset your password"
      title="Reset your password"
      description="Enter the email address associated with your loan and instructions to reset your password will be sent to you."
    >
      {errorAlert}

      <Form onSubmit={onSubmit}>
        <VerticalSpacing spacing={2}>
          <Form.Group controlId="email">
            <ControlledInput
              control={control}
              fieldName="email"
              label="Email"
              inputType="email"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus
            />
          </Form.Group>

          <Button
            type="submit"
            size="lg"
            className="w-100"
            innerClassName="justify-content-center"
            loading={isSubmitting}
          >
            Submit
          </Button>

          <Link className="auth-layout__link auth-layout__link--center" to={PORTAL_LOGIN_PATH}>
            Back to login
          </Link>
        </VerticalSpacing>
      </Form>
    </AuthLayout>
  );
};
